import { styled } from "styled-components";
import { CSSProperties, ReactNode } from "react";

interface FlexProps {
  children: ReactNode;
  direction?: "row" | "column";
  justify?: CSSProperties["justifyContent"];
  align?: CSSProperties["alignItems"];
  gap?: number | string;
  wrap?: CSSProperties["flexWrap"];
  style?: CSSProperties;
  vertical?: boolean; // Ant Design compatibility
  className?: string;
}

const FlexContainer = styled.div<{
  $direction: string;
  $justify?: string;
  $align?: string;
  $gap?: string | number;
  $wrap?: string;
}>`
  display: flex;
  flex-direction: ${({ $direction }) => $direction};
  justify-content: ${({ $justify }) => $justify || "flex-start"};
  align-items: ${({ $align }) => $align || "stretch"};
  gap: ${({ $gap }) => (typeof $gap === "number" ? `${$gap}px` : $gap || "0")};
  flex-wrap: ${({ $wrap }) => $wrap || "nowrap"};
`;

export const Flex = ({
  children,
  direction = "row",
  justify,
  align,
  gap,
  wrap,
  style,
  vertical,
  className,
}: FlexProps) => {
  const finalDirection = vertical ? "column" : direction;

  return (
    <FlexContainer
      $direction={finalDirection}
      $justify={justify}
      $align={align}
      $gap={gap}
      $wrap={wrap}
      style={style}
      className={className}
    >
      {children}
    </FlexContainer>
  );
};

// Grid system for Row/Col replacement
interface RowProps {
  children: ReactNode;
  gutter?: number | [number, number];
  style?: CSSProperties;
}

interface ColProps {
  children: ReactNode;
  span?: number; // 0-24 (Ant Design system)
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  style?: CSSProperties;
}

const RowContainer = styled.div<{ $gutter?: number | [number, number] }>`
  display: flex;
  flex-wrap: wrap;
  margin-left: ${({ $gutter }) => {
    if (Array.isArray($gutter)) return `-${$gutter[0] / 2}px`;
    return $gutter ? `-${$gutter / 2}px` : "0";
  }};
  margin-right: ${({ $gutter }) => {
    if (Array.isArray($gutter)) return `-${$gutter[0] / 2}px`;
    return $gutter ? `-${$gutter / 2}px` : "0";
  }};
`;

const ColContainer = styled.div<{
  $span?: number;
  $xs?: number;
  $sm?: number;
  $md?: number;
  $lg?: number;
  $gutter?: number;
}>`
  flex: 0 0 ${({ $span, $xs }) => {
    const size = $xs || $span;
    return size ? `${(size / 24) * 100}%` : "auto";
  }};
  max-width: ${({ $span, $xs }) => {
    const size = $xs || $span;
    return size ? `${(size / 24) * 100}%` : "100%";
  }};
  padding-left: ${({ $gutter }) => ($gutter ? `${$gutter / 2}px` : "0")};
  padding-right: ${({ $gutter }) => ($gutter ? `${$gutter / 2}px` : "0")};

  @media (min-width: 576px) {
    flex: 0 0 ${({ $sm }) => ($sm ? `${($sm / 24) * 100}%` : "auto")};
    max-width: ${({ $sm }) => ($sm ? `${($sm / 24) * 100}%` : "100%")};
  }

  @media (min-width: 768px) {
    flex: 0 0 ${({ $md }) => ($md ? `${($md / 24) * 100}%` : "auto")};
    max-width: ${({ $md }) => ($md ? `${($md / 24) * 100}%` : "100%")};
  }

  @media (min-width: 992px) {
    flex: 0 0 ${({ $lg }) => ($lg ? `${($lg / 24) * 100}%` : "auto")};
    max-width: ${({ $lg }) => ($lg ? `${($lg / 24) * 100}%` : "100%")};
  }
`;

export const Row = ({ children, gutter, style }: RowProps) => {
  return (
    <RowContainer $gutter={gutter} style={style}>
      {children}
    </RowContainer>
  );
};

export const Col = ({ children, span, xs, sm, md, lg, style }: ColProps) => {
  // Get gutter from parent context if needed
  const gutter = 24; // Default from Ant Design usage

  return (
    <ColContainer $span={span} $xs={xs} $sm={sm} $md={md} $lg={lg} $gutter={gutter} style={style}>
      {children}
    </ColContainer>
  );
};
